import { ProposalJsonEditor } from "./proposal-json-editor";
import { ProposalFileActions } from "./proposal-file-actions.js";
import { useEffect, useState } from "react";
type Item=Record<string,any>;
export type Requester=(route:string,method?:string,body?:unknown)=>Promise<any>;
const explain=(error:unknown)=>error instanceof Error?error.message:String(error);

type ParsedProposalText={
  value:unknown;
  normalized:string;
  changed:boolean;
};

const normalizeSmartJsonQuotes=(input:string)=>{
  let output="";
  let mode:"plain"|"ascii"|"smart"="plain";
  let escaped=false;

  for(const char of input){
    if(mode==="ascii"){
      output+=char;
      if(escaped){escaped=false;continue;}
      if(char==="\\"){escaped=true;continue;}
      if(char==='"')mode="plain";
      continue;
    }

    if(mode==="smart"){
      if(char==="”"){output+='"';mode="plain";continue;}
      if(char==='"'){output+='\\"';continue;}
      output+=char;
      continue;
    }

    if(char==='"'){output+=char;mode="ascii";continue;}
    if(char==="“"){output+='"';mode="smart";continue;}
    if(char==="”"){output+='"';continue;}
    if(char==="\u00a0"){output+=" ";continue;}
    output+=char;
  }

  return output;
};

const stripTrailingJsonCommas=(input:string)=>{
  let output="";
  let inString=false;
  let escaped=false;

  for(let index=0;index<input.length;index++){
    const char=input[index];

    if(inString){
      output+=char;
      if(escaped){escaped=false;continue;}
      if(char==="\\"){escaped=true;continue;}
      if(char==='"')inString=false;
      continue;
    }

    if(char==='"'){
      inString=true;
      output+=char;
      continue;
    }

    if(char===","){
      let next=index+1;
      while(next<input.length&&/\s/.test(input[next]))next++;
      if(input[next]==="}"||input[next]==="]")continue;
    }

    output+=char;
  }

  return output;
};

const parseProposalText=(raw:string):ParsedProposalText=>{
  const original=raw.replace(/^\uFEFF/,"").trim();
  if(!original)throw new Error("Proposal JSON is empty.");

  let candidate=original;
  const fences=[...original.matchAll(/```(?:json)?\s*([\s\S]*?)```/gi)];

  if(fences.length>1){
    throw new Error("Return contains multiple fenced blocks. Paste exactly one Proposal object.");
  }

  if(fences.length===1){
    const outside=original.replace(fences[0][0],"").trim();
    if(/[{}]/.test(outside)){
      throw new Error("Return contains more than one possible Proposal object.");
    }
    candidate=fences[0][1].trim();
  }else{
    try{
      const value=JSON.parse(candidate);
      const normalized=JSON.stringify(value,null,2);
      if(!normalized)throw new Error("Proposal JSON did not produce an object.");
      return {value,normalized,changed:normalized!==original};
    }catch{}

    const first=original.indexOf("{");
    const last=original.lastIndexOf("}");
    if(first<0||last<=first){
      throw new Error("Return must contain exactly one Proposal JSON object.");
    }
    candidate=original.slice(first,last+1).trim();
  }

  const repaired=stripTrailingJsonCommas(normalizeSmartJsonQuotes(candidate));
  const attempts=[candidate,repaired];
  let lastError:unknown=null;

  for(const attempt of [...new Set(attempts)]){
    try{
      const value=JSON.parse(attempt);
      const normalized=JSON.stringify(value,null,2);
      if(!normalized)throw new Error("Proposal JSON did not produce an object.");
      return {value,normalized,changed:normalized!==original};
    }catch(cause){
      lastError=cause;
    }
  }

  throw new Error(
    `Proposal JSON could not be parsed after safe transport normalization: ${
      lastError instanceof Error?lastError.message:String(lastError)
    }`
  );
};
export function PublicationReview({request,roots,onComplete}:{request:Requester;roots:string[];onComplete?:(result:Item)=>void}){
  const [plan,setPlan]=useState<Item|null>(null),[reason,setReason]=useState(""),[error,setError]=useState(""),[busy,setBusy]=useState(false),[notice,setNotice]=useState("");
  useEffect(()=>{setPlan(null);setNotice("");},[roots.join("|")]);
  const preview=async()=>{setBusy(true);setError("");try{setPlan(await request("/publication/preview","POST",{roots}));}catch(cause){setError(explain(cause));}finally{setBusy(false);}};
  const approve=async()=>{if(!plan)return;setBusy(true);setError("");try{const value=await request("/publication/approve-preview","POST",{roots:plan.roots,digest:plan.digest,reason});setNotice(`${value.approved.length} publication decisions recorded. No Release was built or activated.`);setPlan(null);onComplete?.(value);}catch(cause){setError(explain(cause));setPlan(null);}finally{setBusy(false);}};
  const groups=[...new Set<string>((plan?.resources??[]).map((row:Item)=>String(row.kind)))];
  return <section className="subpanel publication-review"><h3>Review the complete publication set</h3><p>Required canonical evidence is included in this exact review. A required private dependency remains a blocker.</p><div className="publication-preview-actions"><button disabled={busy||!roots.length} onClick={()=>void preview()}>{plan?"Regenerate exact preview":"Preview selected outputs and dependencies"}</button><span>{roots.length} selected outputs</span></div>{error&&<p role="alert" className="alert">{error} Your selection is retained; regenerate before approving.</p>}{notice&&<p role="status" className="notice">{notice}</p>}{plan&&<><div className="publication-summary"><strong>{plan.resources.filter((row:Item)=>row.decision_needed).length} need approval</strong><span>{plan.resources.filter((row:Item)=>!row.decision_needed).length} already approved</span><span>{plan.excluded?.length??0} internal-only</span><span>{plan.blockers.length} blockers · revision {plan.knowledge_revision}</span></div><details><summary>Exact reviewed revision digest</summary><code>{plan.digest}</code></details><div className="publication-decision-bar"><label>Review reason<input value={reason} onChange={event=>setReason(event.target.value)} maxLength={4000}/></label><button disabled={busy||!plan.valid||!reason.trim()} onClick={()=>void approve()}>Approve only this reviewed set</button></div>{plan.blockers.map((row:Item,index:number)=><p className="alert" key={index}>{row.id}: {row.reason}</p>)}{groups.map(group=><details className="publication-group" key={group} open={group!=="source"}><summary>{group} · {plan.resources.filter((row:Item)=>row.kind===group).length}</summary><ul>{plan.resources.filter((row:Item)=>row.kind===group).map((row:Item)=><li key={row.id}><strong>{row.title}</strong><small>{String(row.origin).replaceAll("_"," ")} · {row.decision_needed?"approval needed":"already approved"}</small><details><summary>Identity and revision</summary><code>{row.id}</code><p>Revision {row.revision} · {row.disclosure}</p></details></li>)}</ul></details>)}</>}</section>;
}
export function ProposalImporter({request,onImported}:{request:Requester;onImported?:(value:Item)=>void}){
  const [text,setText]=useState(""),[fileName,setFileName]=useState("diggingbeagle-proposal.json"),[check,setCheck]=useState<Item|null>(null),[busy,setBusy]=useState(false),[error,setError]=useState("");
  const edit=(value:string)=>{setText(value);setCheck(null);setError("");};
  const validate=async()=>{setBusy(true);setError("");try{const result=await request("/proposals/preflight","POST",{raw_text:text});if(result.transport_normalized&&typeof result.normalized_text==="string")setText(result.normalized_text);setCheck(result);}catch(cause){setError(explain(cause));setCheck(null);}finally{setBusy(false);}};
  const importResult=async()=>{setBusy(true);setError("");try{const value=await request("/proposals/import","POST",{raw_text:text});if(value.transport_normalized&&typeof value.normalized_text==="string")setText(value.normalized_text);onImported?.(value);setCheck(null);}catch(cause){setError(explain(cause));}finally{setBusy(false);}};
  return <section className="subpanel"><h3>Validate returned research</h3><label>JSON file<input type="file" accept="application/json,text/plain,.json,.txt" onChange={event=>{const file=event.target.files?.[0];if(!file)return;if(file.size>5*1024*1024){setError("JSON file exceeds 5 MB. Use a bounded research return.");return;}void file.text().then(value=>{setFileName(file.name);edit(value);}).catch(cause=>setError(explain(cause)));}}/></label><label>Or paste Proposal JSON<ProposalJsonEditor value={text} onChange={edit}/></label><ProposalFileActions value={text} filename={fileName}/><div className="actions"><button disabled={busy||!text.trim()} onClick={()=>void validate()}>Check without importing</button><button disabled={busy||!check?.valid} onClick={()=>void importResult()}>Import validated package</button></div>{busy&&<p role="status">Checking the exact package…</p>}{error&&<p role="alert" className="alert">{error}</p>}{check&&<>{check.transport_normalized&&<p className="notice">Harmless return formatting was normalized before validation. Review the normalized JSON above.</p>}<p className={check.valid?"notice":"alert"}>{check.valid?"Structure and references pass. This is not factual or publication approval.":"Fix the listed fields. No knowledge was changed."}</p>{check.issues?.map((issue:Item,index:number)=><p className="audit-issue" key={index}><code>{issue.operation_id??""} {issue.path??""}</code><br/>{issue.message}</p>)}</>}</section>;
}
